/**
 * DEMO · Idea 9 — Enlace para tu cliente.
 *
 * Vista ligera pensada para celular, con dos pestañas:
 *  - Presupuesto: el edificio pintado por partida (con leyenda); tocas una pieza y ves su partida y su
 *    importe; abajo, lo que cuesta cada partida y el total.
 *  - Avance de obra: lo ejecutado al corte (los mismos datos del demo 4D): piezas hechas, a medias y
 *    pendientes, e importe ejecutado por partida.
 * Real (del modelo): piezas, a qué partida van y sus cantidades. De ejemplo: el enlace, el cliente, los
 * conceptos, los precios y el avance reportado.
 */
import { useMemo, useState } from 'react';
import { Tab, TabList } from '@fluentui/react-components';
import { DemoVisor3D, type Pintura } from '../../components/demos/DemoVisor3D.js';
import { DemoMarco, NotaEjemplo, Panel } from '../../components/demos/DemoMarco.js';
import type { ElementoDemo, ModeloDemo } from '../../components/demos/modeloDemo.js';
import {
  HP_EJEMPLO,
  cantidadDe,
  colorHex,
  coloresDemo,
  conceptoDe,
  crearContexto,
  etiquetaElemento,
  pesos,
  type ConceptoEjemplo,
} from '../../components/demos/logicaDemo.js';
import { CORTE, armarEtapas, avancesReportados, conceptosDelModelo, corteDe } from '../../components/demos/avanceObra.js';
import { LoQueFalta } from '../../components/demos/LoQueFalta.js';
import datos from '../../components/demos/datos/enlace-para-tu-cliente.json';
import catalogo from '../../components/demos/datos/catalogo.json';

const FICHA = catalogo.demos.find((d) => d.id === 'enlace-para-tu-cliente')!;
type Pestana = 'presupuesto' | 'avance';

export function DemoEnlaceCliente() {
  const [modelo, setModelo] = useState<ModeloDemo | null>(null);
  const [tocado, setTocado] = useState<string | null>(null);
  const [pestana, setPestana] = useState<Pestana>('presupuesto');
  const colores = useMemo(() => coloresDemo(), []);

  const r = useMemo(() => {
    if (!modelo) return null;
    const ctx = crearContexto(modelo.elementos);
    const de = new Map<string, { c: ConceptoEjemplo; importe: number }>();
    const porPartida = new Map<string, number>();
    for (const e of modelo.elementos) {
      const c = conceptoDe(e, ctx);
      if (!c) continue;
      const importe = cantidadDe(e, c.magnitud) * c.pu;
      de.set(e.clave, { c, importe });
      porPartida.set(c.partida, (porPartida.get(c.partida) ?? 0) + importe);
    }
    const total = [...porPartida.values()].reduce((a, b) => a + b, 0);
    // Avance reportado al corte (datos de ejemplo del 4D), pieza por pieza.
    const etapas = armarEtapas(modelo);
    const avance = avancesReportados(etapas, conceptosDelModelo(modelo));
    const ejecutadoPorPartida = new Map<string, number>();
    for (const [clave, x] of de) {
      const f = avance.get(clave)?.f ?? 0;
      ejecutadoPorPartida.set(x.c.partida, (ejecutadoPorPartida.get(x.c.partida) ?? 0) + f * x.importe);
    }
    const ejecutado = [...ejecutadoPorPartida.values()].reduce((a, b) => a + b, 0);
    return { de, porPartida, total, avance, ejecutadoPorPartida, ejecutado };
  }, [modelo]);

  const partidas = HP_EJEMPLO.partidas.filter((p) => r?.porPartida.has(p.id));
  const colorDe = useMemo(() => new Map(HP_EJEMPLO.partidas.map((p) => [p.id, colorHex(p.color)])), []);
  const pintar = useMemo(
    () =>
      (el: ElementoDemo | null, clave: string): Pintura => {
        if (!el) return { visible: false };
        const x = r?.de.get(clave);
        if (!x) return { color: colores.neutro, opacidad: 0.12 };
        const color = colorDe.get(x.c.partida) ?? null;
        const tenue = tocado != null && tocado !== clave;
        if (pestana === 'presupuesto') return { color, opacidad: tenue ? 0.3 : 1 };
        const a = r?.avance.get(clave);
        const f = a?.f ?? 0;
        if (f >= 1) return { color, opacidad: tenue ? 0.3 : 1 };
        if (f > 0 && a) {
          const c = corteDe(el, f, a.crece);
          if (c) return { color, opacidad: 1, recorte: { ...c, fantasma: { color: colores.neutro, opacidad: 0.12 } } };
        }
        return { color: colores.neutro, opacidad: 0.1 };
      },
    [r, tocado, colorDe, pestana, colores],
  );
  const sel = tocado ? r?.de.get(tocado) : undefined;
  const selEl = tocado ? modelo?.porClave.get(tocado) : undefined;
  const partidaSel = sel ? HP_EJEMPLO.partidas.find((p) => p.id === sel.c.partida) : undefined;
  const fSel = tocado ? (r?.avance.get(tocado)?.f ?? 0) : 0;

  const Punto = ({ color }: { color: string }) => (
    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: color, marginRight: 6, flexShrink: 0 }} />
  );

  return (
    <DemoMarco titulo={FICHA.nombre} dolor={FICHA.dolor}>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Marco de celular: así lo vería tu cliente */}
        <div
          style={{
            width: 390,
            maxWidth: '100%',
            border: '10px solid var(--bg-raised)',
            borderRadius: 36,
            overflow: 'hidden',
            background: 'var(--bg-page)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          }}
        >
          <div style={{ padding: '12px 14px 4px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{datos.vigencia}</div>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-400)' }}>{datos.obra}</div>
            <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>
              {datos.cliente} · de {datos.constructora}
            </div>
          </div>
          <TabList selectedValue={pestana} onTabSelect={(_, d) => setPestana(d.value as Pestana)} style={{ padding: '0 6px' }}>
            <Tab value="presupuesto">Presupuesto</Tab>
            <Tab value="avance">Avance de obra</Tab>
          </TabList>
          <DemoVisor3D
            fuentes={[{ clave: 'a', url: '/demos/fzk-haus-a.ifc' }]}
            onListo={(m) => setModelo(m.a ?? null)}
            pintar={pintar}
            onPick={(c) => setTocado(c && r?.de.has(c) ? c : null)}
            alto={300}
            desde={[1, 0.9, 1.3]}
          />
          {/* Leyenda: qué color es cada partida */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', padding: '8px 14px 0', fontSize: 11, color: 'var(--text-secondary)' }}>
            {partidas.map((p) => (
              <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Punto color={p.color} />
                {p.nombre}
              </span>
            ))}
            {pestana === 'avance' && (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Punto color="var(--text-tertiary)" />
                Por construir (tenue)
              </span>
            )}
          </div>
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ minHeight: 58, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '10px 12px' }}>
              {sel && partidaSel && selEl ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-400)', display: 'flex', alignItems: 'center' }}>
                    <Punto color={partidaSel.color} />
                    {partidaSel.nombre} · {pesos(sel.importe)}
                  </div>
                  <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>
                    {sel.c.descripcion} — {etiquetaElemento(selEl)}
                    {pestana === 'avance' ? ` · ${Math.round(fSel * 100)} % construido` : ''}
                  </div>
                </>
              ) : (
                <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-300)' }}>
                  {pestana === 'presupuesto' ? 'Toca una parte del edificio para ver cuánto cuesta.' : 'Toca una parte del edificio para ver cuánto lleva construido.'}
                </div>
              )}
            </div>

            {r && pestana === 'presupuesto' && (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <span style={{ flex: 1, color: 'var(--text-secondary)' }}>Total del presupuesto</span>
                  <span style={{ fontWeight: 700, fontSize: 'var(--fs-500)' }}>{pesos(r.total)}</span>
                </div>
                {partidas.map((p) => {
                  const v = r.porPartida.get(p.id)!;
                  return (
                    <div key={p.id}>
                      <div style={{ display: 'flex', fontSize: 'var(--fs-300)', alignItems: 'center' }}>
                        <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center' }}>
                          <Punto color={p.color} />
                          {p.nombre}
                        </span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pesos(v)}</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-raised)', marginTop: 3 }}>
                        <div style={{ width: `${(v / r.total) * 100}%`, height: '100%', background: p.color, borderRadius: 3 }} />
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {r && pestana === 'avance' && (
              <>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Avance al corte: {CORTE.fecha}</div>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <span style={{ flex: 1, color: 'var(--text-secondary)' }}>Ejecutado</span>
                  <span style={{ fontWeight: 700, fontSize: 'var(--fs-500)' }}>
                    {pesos(r.ejecutado)} <span style={{ fontSize: 'var(--fs-300)', fontWeight: 500 }}>({Math.round((r.ejecutado / r.total) * 100)} %)</span>
                  </span>
                </div>
                {partidas.map((p) => {
                  const tot = r.porPartida.get(p.id)!;
                  const ej = r.ejecutadoPorPartida.get(p.id) ?? 0;
                  return (
                    <div key={p.id}>
                      <div style={{ display: 'flex', fontSize: 'var(--fs-300)', alignItems: 'center' }}>
                        <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center' }}>
                          <Punto color={p.color} />
                          {p.nombre}
                        </span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round((ej / tot) * 100)} %</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-raised)', marginTop: 3 }}>
                        <div style={{ width: `${(ej / tot) * 100}%`, height: '100%', background: p.color, borderRadius: 3 }} />
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                        {pesos(ej)} de {pesos(tot)}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 280, maxWidth: 620 }}>
          <Panel titulo="Cómo funcionaría">
            <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <li>Desde tu presupuesto presionas "Compartir con mi cliente" y te da un enlace privado.</li>
              <li>Tu cliente lo abre en su celular, sin instalar nada ni crear cuenta.</li>
              <li>
                <b>Presupuesto</b>: ve el edificio pintado por partida y, al tocar una parte, cuánto cuesta.
              </li>
              <li>
                <b>Avance de obra</b>: ve lo que ya está construido y cuánto dinero representa, con el último avance que reportaste.
              </li>
              <li>
                Tú decides qué ve: en este ejemplo {datos.mostrarPreciosUnitarios ? 'sí' : 'no'} se muestran los precios unitarios, solo los
                importes.
              </li>
              <li>El enlace vence solo y puedes apagarlo cuando quieras.</li>
            </ul>
            <NotaEjemplo>{datos.aviso}</NotaEjemplo>
          </Panel>
          <div style={{ marginTop: 14, fontWeight: 600 }}>Antes de compartirlo, revisa (esto no lo ve tu cliente):</div>
          <LoQueFalta modelo={modelo} conceptosEnHoja={r ? [...new Set([...r.de.values()].map((x) => x.c.id))] : []} />
        </div>
      </div>
    </DemoMarco>
  );
}
